// components/MyPage/MyPointHistory.jsx
// ────────────────────────────────────────────────────────────────
// • 금액 부호 기준으로 earn / use / neutral 구분
// • 0 pt → 회색 “변동 없음” + 0pt
// • 금액 1,000pt 형식 유지
// ----------------------------------------------------------------
// components/MyPage/MyPointHistory.jsx
import { useEffect, useState } from 'react';
import {
    Box, Typography, Card, CardContent, Stack,
    CircularProgress, Pagination, useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import dayjs from 'dayjs';
import api from '../../api/axios';
import SafeChip from '../Common/SafeChip';
// ✅ 1. 설명 번역 맵 추가
const DESCRIPTION_MAP = {
    'UNLOCK:widget.reactionBar': '미니홈 응원 위젯 해금',
    'UNLOCK:theme.background.emote': '미니홈 이모티콘 배경 해금',
    'UNLOCK:widget.titleShowcase': '미니홈 칭호 진열장 해금',
    'UNLOCK:widget.emoteShelf': '미니홈 이모티콘 진열장 해금',
    'UNLOCK:widget.textBox': '미니홈 텍스트 박스 해금'
};

// ✅ 2. 설명 변환 함수 추가
const formatDescription = (description) => {
    const formatted = DESCRIPTION_MAP[description] || description;
    return formatted || '내용 없음'; // 내용이 아예 없는 경우를 대비
};

const PAGE_SIZE = 8;

export default function MyPointHistory() {
    const [logs, setLogs] = useState(null);
    const [page, setPage] = useState(1);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    useEffect(() => {
        api.get('/api/points/history/')
            .then(r => setLogs(r.data.histories || []))
            .catch(() => setLogs([]));
    }, []);

    if (logs === null) return <CircularProgress />;
    if (!logs.length) return <Typography>포인트 내역이 없습니다.</Typography>;

    const fmtDate = d => {
        const dow = ['일', '월', '화', '수', '목', '금', '토'][dayjs(d).day()];
        return `${dayjs(d).format('YYYY-MM-DD')} (${dow})`;
    };

    const AmountChip = ({ log }) => {
        const { amount } = log;

        let variant = 'neutral';
        if (amount > 0) variant = 'earn';
        else if (amount < 0) variant = 'use';

        const formatted = Math.abs(amount).toLocaleString();
        const label = variant === 'earn' ? '적립' : variant === 'use' ? '차감' : '변동 없음';
        const chipColor = variant === 'earn' ? 'primary' : variant === 'use' ? 'error' : 'default';
        const textColor = variant === 'earn' ? 'primary.main' : variant === 'use' ? 'error.main' : 'text.secondary';
        const sign = variant === 'earn' ? '+' : variant === 'use' ? '-' : '';

        return (
            <Stack direction="row" spacing={0.5} alignItems="center">
                <SafeChip label={label} color={chipColor} size="small" />
                <Typography variant="subtitle2" fontWeight={700} sx={{ color: textColor }}>
                    {`${sign}${formatted} Leaf🌿`}
                </Typography>
            </Stack>
        );
    };

    const pageCount = Math.ceil(logs.length / PAGE_SIZE);
    const slice = logs.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    return (
        <Box sx={{ maxWidth: 560, mx: 'auto' }}>
            <Typography variant="h6" fontWeight="bold" mb={2}>
                📜 포인트 내역
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {slice.map((log, index) => {
                    const uniqueKey = log.id || `${log.date}-${log.amount}-${index}`;
                    const translatedDesc = formatDescription(log.description);

                    // ✅ log.description 대신 translatedDesc를 사용하도록 수정
                    const shortDesc =
                        isMobile && translatedDesc.length > 12
                            ? translatedDesc.slice(0, 12) + '…'
                            : translatedDesc;

                    return (
                        <Card
                            key={uniqueKey}
                            variant="outlined"
                            sx={{ p: 1, '&:hover': { boxShadow: 3, transition: '.2s' } }}
                        >
                            <CardContent sx={{ py: 0.75, '&:last-child': { pb: 0.75 } }}>
                                <AmountChip log={log} />
                                <Typography
                                    variant="body2"
                                    fontWeight={600}
                                    // ✅ title 속성에도 translatedDesc를 사용
                                    title={translatedDesc}
                                    mt={0.25}
                                >
                                    {shortDesc}
                                </Typography>
                            </CardContent>
                        </Card>
                    );
                })}
            </Box>

            {pageCount > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                    <Pagination
                        count={pageCount}
                        page={page}
                        onChange={(_, v) => setPage(v)}
                        showFirstButton
                        showLastButton
                        siblingCount={1}
                        size="small"
                    />
                </Box>
            )}
        </Box>
    );
}